import React from 'react';
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Box,
  Grid,
  ProgressBar,
  DataTable,
  Tooltip
} from '@shopify/polaris';
import { ArrowUpIcon, ArrowDownIcon, InfoIcon } from '@shopify/polaris-icons';

interface WidgetItem {
  key: string;
  value: any;
  [key: string]: any;
}

interface WidgetRendererProps {
  widgetType: string;
  items: WidgetItem[];
  title: string;
}

const WidgetRenderer: React.FC<WidgetRendererProps> = ({ widgetType, items, title }) => {
  // Helper function to format keys (remove underscores, titleize)
  const formatKey = (key: string): string => {
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Normalize any incoming value to a safe displayable primitive
  const normalizeValue = (value: any): string => {
    if (value === null || value === undefined) return '';
    const t = typeof value;
    if (t === 'string' || t === 'number' || t === 'boolean') return String(value);
    if (t === 'object') {
      // Common shapes from APIs: { default: ... }, { value: ... }, { text: ... }
      if (Object.prototype.hasOwnProperty.call(value, 'default')) {
        return normalizeValue((value as any).default);
      }
      if (Object.prototype.hasOwnProperty.call(value, 'value')) {
        return normalizeValue((value as any).value);
      }
      if (Object.prototype.hasOwnProperty.call(value, 'text')) {
        return normalizeValue((value as any).text);
      }
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  // Helper function to get score color
  const getScoreColor = (score: number, max: number = 10) => {
    const percentage = (score / max) * 100;
    if (percentage >= 80) return '#00a047'; // Green
    if (percentage >= 60) return '#ffc453'; // Yellow
    return '#d72c0d'; // Red
  };

  // Transcript & Translation tab content
  const renderTranscriptTab = () => {
    // Flatten items to key/value pairs for easier lookup
    const allPairs: Array<[string, any]> = Array.isArray(items)
      ? items.flatMap((it: any) => Object.entries(it))
      : [];

    const findByKeys = (keys: string[]) => {
      const lowerKeys = keys.map(k => k.toLowerCase());
      const match = allPairs.find(([k]) => lowerKeys.includes(k.toLowerCase()));
      return match ? String(match[1] ?? '') : '';
    };

    const transcript = findByKeys(['transcript', 'transcription', 'transcript_text']);
    const translation = findByKeys(['translation', 'translated_text']);

    const hasAny = Boolean(transcript || translation);

    return (
      <Card padding="500">
        <BlockStack gap="400">
          <Text variant="headingMd" as="h4">Transcript & Translation</Text>
          {!hasAny && (
            <Text variant="bodyMd" tone="subdued" as="p">
              No transcript or translation available for this item.
            </Text>
          )}
          {transcript && (
            <BlockStack gap="200">
              <Text variant="headingSm" as="h5">Transcript</Text>
              <Box padding="300" background="bg-surface-secondary">
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                  <Text variant="bodyMd" as="p">{transcript}</Text>
                </div>
              </Box>
            </BlockStack>
          )}
          {translation && (
            <BlockStack gap="200">
              <Text variant="headingSm" as="h5">Translation</Text>
              <Box padding="300" background="bg-surface-secondary">
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                  <Text variant="bodyMd" as="p">{translation}</Text>
                </div>
              </Box>
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    );
  };

  // Helper function to get sentiment color
  const getSentimentColor = (sentiment: string) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive': return '#00a047';
      case 'negative': return '#d72c0d';
      case 'neutral': return '#ffc453';
      default: return '#6d7175';
    }
  };

  // Helper function to get sentiment badge tone
  const getSentimentBadgeTone = (sentiment: string): 'success' | 'warning' | 'critical' | 'info' => {
    switch (sentiment?.toLowerCase()) {
      case 'positive': return 'success';
      case 'negative': return 'critical';
      case 'neutral': return 'warning';
      default: return 'info';
    }
  };

  // Helper function to get priority badge tone
  const getPriorityBadgeTone = (priority: string): 'critical' | 'warning' | 'success' | 'info' => {
    const p = priority?.toLowerCase?.() || '';
    if (p === 'high' || p === 'urgent') return 'critical';
    if (p === 'medium' || p === 'normal') return 'warning';
    if (p === 'low') return 'success';
    return 'info';
  };

  // Helper function to extract percentage from value
  const extractPercentage = (value: any): number => {
    // Unwrap nested common containers first
    if (value && typeof value === 'object') {
      if ('default' in value) return extractPercentage((value as any).default);
      if ('value' in value) return extractPercentage((value as any).value);
      if ('text' in value) return extractPercentage((value as any).text);
    }
    if (typeof value === 'string') {
      const match = value.match(/(\d+)%/);
      return match ? parseInt(match[1]) : 0;
    }
    if (typeof value === 'number') {
      return value;
    }
    return 0;
  };

  // Simple Score Cards - Individual cards for each score with donut charts
  const renderSimpleScoreCards = () => {
    const titleItem = items.find(item => item.key === 'title');
    const scoresItem = items.find(item => item.key === 'scores');
    const scores = scoresItem?.value || items.filter(item => item.key !== 'title');

    return (
      <Card padding="500">
        <BlockStack gap="400">
          {/* Title */}
          {titleItem && (
            <Text variant="headingLg" as="h3">
              {normalizeValue(titleItem.value)}
            </Text>
          )}

          {/* Score Cards Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '12px'
          }}>
            {scores.map((item: any, index: number) => {
              const percentage = extractPercentage(item.value);
              const color = getScoreColor(percentage, 100);
              
              return (
                <div key={index} style={{
                  border: '1px solid #e1e3e5',
                  borderRadius: '8px',
                  backgroundColor: '#f6f6f7',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '160px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <Text variant="headingMd" as="h4" alignment="center">
                      {formatKey(item.key)}
                    </Text>
                  </div>
                  
                  {/* Donut Chart */}
                  <div style={{ position: 'relative', width: '80px', height: '80px', marginBottom: '12px' }}>
                    <svg width="80" height="80" style={{ transform: 'rotate(-90deg)' }}>
                      <circle
                        cx="40"
                        cy="40"
                        r="30"
                        fill="none"
                        stroke="#e1e3e5"
                        strokeWidth="6"
                      />
                      <circle
                        cx="40"
                        cy="40"
                        r="30"
                        fill="none"
                        stroke={color}
                        strokeWidth="6"
                        strokeDasharray={`${2 * Math.PI * 30}`}
                        strokeDashoffset={`${2 * Math.PI * 30 * (1 - percentage / 100)}`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: color,
                      textAlign: 'center'
                    }}>
                      {percentage}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </BlockStack>
      </Card>
    );
  };

  // Summary Card - Just a card with paragraph
  const renderSummaryCard = () => {
    const titleItem = items.find(item => item.key === 'title');
    const highlightItem = items.find(item => item.key === 'highlight');
    const summaryItem = items.find(item => item.key === 'summary');

    return (
      <Card padding="500">
        <BlockStack gap="400">
          {titleItem && (
            <Text variant="headingLg" as="h3">
              {normalizeValue(titleItem.value)}
            </Text>
          )}
          
          {highlightItem && (
            <InlineStack gap="200">
              <Text variant="bodyMd" as="span">Status:</Text>
              <Badge tone={getSentimentBadgeTone(normalizeValue(highlightItem.value))}>
                {normalizeValue(highlightItem.value)}
              </Badge>
            </InlineStack>
          )}
          
          {summaryItem && (
            <Box padding="400" background="bg-surface-secondary">
              <div style={{ lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                <Text variant="bodyMd" as="p">
                  {normalizeValue(summaryItem.value)}
                </Text>
              </div>
            </Box>
          )}
        </BlockStack>
      </Card>
    );
  };

  // Detailed Score Cards - One card with overall score on left (30-35%) and detailed scores on right
  const renderDetailedScoreCards = () => {
    const titleItem = items.find(item => item.key === 'title');
    const overallScoreItem = items.find(item => item.key === 'overall_score');
    const scoresItem = items.find(item => item.key === 'scores');

    return (
      <BlockStack gap="500">
        {/* Single Card with Title, Overall Score on Left and Detailed Scores on Right */}
        <Card padding="500">
          <BlockStack gap="500">
            {/* Title */}
            {titleItem && (
              <Text variant="headingLg" as="h3">
                {normalizeValue(titleItem.value)}
              </Text>
            )}

            <InlineStack gap="500" align="start">
            {/* Overall Score - Left Side (30-35% width) */}
            {overallScoreItem && overallScoreItem.value && (
              <div style={{ width: '25%', minWidth: '200px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <BlockStack gap="500" align="center">
                  <Text variant="headingMd" as="h4" alignment="center">
                    {formatKey(normalizeValue(overallScoreItem.value.key))}
                  </Text>
                  
                  <div style={{ position: 'relative', width: '140px', height: '140px', margin: '0 auto' }}>
                    <svg width="140" height="140" style={{ transform: 'rotate(-90deg)' }}>
                      <circle
                        cx="70"
                        cy="70"
                        r="55"
                        fill="none"
                        stroke="#e1e3e5"
                        strokeWidth="10"
                      />
                      <circle
                        cx="70"
                        cy="70"
                        r="55"
                        fill="none"
                        stroke={getScoreColor(parseInt(normalizeValue(overallScoreItem.value.value)), 10)}
                        strokeWidth="10"
                        strokeDasharray={`${2 * Math.PI * 55}`}
                        strokeDashoffset={`${2 * Math.PI * 55 * (1 - parseInt(normalizeValue(overallScoreItem.value.value)) / 10)}`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                      lineHeight: '1.2'
                    }}>
                      <div style={{
                        fontSize: '28px',
                        fontWeight: 'bold',
                        color: getScoreColor(parseInt(normalizeValue(overallScoreItem.value.value)), 10),
                        marginBottom: '4px'
                      }}>
                        {normalizeValue(overallScoreItem.value.value)}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: '#6d7175',
                        fontWeight: '500'
                      }}>
                        out of 10
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ 
                    textAlign: 'center',
                    padding: '12px 16px',
                    backgroundColor: '#f6f6f7',
                    borderRadius: '8px',
                    border: '1px solid #e1e3e5'
                  }}>
                    <Text variant="bodySm" tone="subdued" alignment="center" as="span">
                      Overall Performance
                    </Text>
                  </div>
                </BlockStack>
              </div>
            )}

            {/* Detailed Scores - Right Side (65-70% width) */}
            {scoresItem && scoresItem.value && Array.isArray(scoresItem.value) && (
              <div style={{ width: '75%', flex: 1 }}>
                <BlockStack gap="400">
                  <Text variant="headingMd" as="h4">Detailed Breakdown</Text>
                  {scoresItem.value.map((score: any, index: number) => {
                    const scoreValue = parseInt(normalizeValue(score.value.value));
                    const color = getScoreColor(scoreValue, 10);
                    
                    return (
                      <BlockStack key={index} gap="200">
                        <InlineStack align="space-between">
                          <Text variant="bodyMd" as="span">
                            {formatKey(normalizeValue(score.key))}
                          </Text>
                          <Text variant="bodyMd" as="span" fontWeight="bold">
                            {scoreValue}/10
                          </Text>
                        </InlineStack>
                        
                        <div style={{ position: 'relative' }}>
                          <ProgressBar
                            progress={scoreValue / 10}
                            size="small"
                          />
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            height: '100%',
                            width: `${(scoreValue / 10) * 100}%`,
                            backgroundColor: color,
                            opacity: 0.8,
                            borderRadius: '4px'
                          }} />
                        </div>
                        
                        {score.value.details && (
                          <Text variant="bodySm" tone="subdued" as="p">
                            {normalizeValue(score.value.details)}
                          </Text>
                        )}
                      </BlockStack>
                    );
                  })}
                </BlockStack>
              </div>
            )}
            </InlineStack>
          </BlockStack>
        </Card>
      </BlockStack>
    );
  };

  // Object List - Rich, responsive row layout
  const renderObjectList = () => {
    if (!items || items.length === 0) return null;

    // Determine present columns with preferred order
    const preferred = ['actionable', 'action', 'due_date', 'dueDate', 'date', 'priority', 'description', 'details'];
    const present = Array.from(new Set(items.flatMap(i => Object.keys(i))));
    const ordered = [
      ...preferred.filter(k => present.some(p => p.toLowerCase() === k.toLowerCase())),
      ...present.filter(p => !preferred.some(k => k.toLowerCase() === p.toLowerCase()))
    ];

    // Helper: normalize key matching ignoring case
    const getValue = (obj: any, key: string) => {
      const realKey = Object.keys(obj).find(k => k.toLowerCase() === key.toLowerCase());
      return realKey ? obj[realKey] : undefined;
    };

    // Grid column sizing; description should take the remaining width
    const getColumnWidth = (key: string) => {
      const k = key.toLowerCase();
      if (k.includes('action')) return '160px';
      if (k.includes('date')) return '150px';
      if (k.includes('priority')) return '120px';
      if (k.includes('description') || k.includes('detail')) return '1fr';
      return '160px';
    };

    const gridTemplateColumns = ordered.map(getColumnWidth).join(' ');

    const renderCell = (key: string, value: any) => {
      if (value === null || value === undefined) return '';
      const k = key.toLowerCase();

      if (k.includes('priority')) {
        return (
          <Badge tone={getPriorityBadgeTone(normalizeValue(value))}>{normalizeValue(value)}</Badge>
        );
      }

      if (k.includes('date')) {
        try {
          const d = new Date(normalizeValue(value));
          return isNaN(d.getTime()) ? normalizeValue(value) : d.toLocaleDateString();
        } catch {
          return normalizeValue(value);
        }
      }

      if (k.includes('action')) {
        return (
          <span style={{
            background: '#e8f0fe',
            color: '#1a73e8',
            borderRadius: 9999,
            padding: '4px 10px',
            fontSize: 12,
            fontWeight: 600
          }}>
            {formatKey(normalizeValue(value))}
          </span>
        );
      }

      if (k.includes('description') || k.includes('detail')) {
        const text = normalizeValue(value);
        return (
          <Tooltip content={text}>
            <span style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              cursor: 'help'
            }}>
              {text}
            </span>
          </Tooltip>
        );
      }

      return normalizeValue(value);
    };

    return (
      <Card padding="500">
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd" as="h4">{title}</Text>
            <Badge tone="info">{`${items.length} item${items.length === 1 ? '' : 's'}`}</Badge>
          </InlineStack>

          {/* Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns,
            gap: 12,
            padding: '10px 12px',
            border: '1px solid #e1e3e5',
            borderRadius: 8,
            background: '#fafbfb',
            fontWeight: 600,
            color: '#303236'
          }}>
            {ordered.map((key) => (
              <span key={key}>{formatKey(key)}</span>
            ))}
          </div>

          {/* Rows */}
          <BlockStack gap="200">
            {items.map((item, idx) => (
              <div key={idx} style={{
                display: 'grid',
                gridTemplateColumns,
                gap: 12,
                padding: '12px',
                border: '1px solid #e1e3e5',
                borderRadius: 8,
                background: '#ffffff',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
              }}>
                {ordered.map((key) => (
                  <div key={key} style={{ minWidth: 0 }}>
                    {renderCell(key, getValue(item, key))}
                  </div>
                ))}
              </div>
            ))}
          </BlockStack>
        </BlockStack>
      </Card>
    );
  };

  // Tag Cloud Table - Table with score as number, color for sentiment, show as tag/badge
  const renderTagCloudTable = () => {
    if (!items || items.length === 0) return null;

    const headers = ['Category'];
    const gridTemplateColumns = '1fr';

    return (
      <Card padding="500">
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd" as="h4">{title}</Text>
            <Badge tone="info">{`${items.length} item${items.length === 1 ? '' : 's'}`}</Badge>
          </InlineStack>

          {/* Rows */}
          <BlockStack gap="200">
            {items.map((item, idx) => {
              const scoreValue = Number(normalizeValue(item.score)) || 0;
              const scorePct = Math.max(0, Math.min(100, (scoreValue / 10) * 100));
              const scoreColor = getScoreColor(scoreValue, 10);
              const desc = normalizeValue(item.description || '');

              return (
                <div key={idx} style={{
                  border: '1px solid #e1e3e5',
                  borderRadius: 8,
                  background: '#ffffff',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                }}>
                  {/* Category row */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns,
                    gap: 12,
                    padding: '12px 12px 8px 12px'
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <span style={{ fontWeight: 600 }}>
                        {formatKey(normalizeValue(item.category || ''))}
                      </span>
                    </div>
                  </div>
                  
                  {/* Sentiment and Score row */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    padding: '0 12px 8px 12px'
                  }}>
                    <Badge tone={getSentimentBadgeTone(normalizeValue(item.sentiment || ''))}>
                      {formatKey(normalizeValue(item.sentiment || ''))}
                    </Badge>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ position: 'relative', width: 120, flexShrink: 0 }}>
                        <div style={{ height: 8, width: '100%', background: '#eef0f2', borderRadius: 9999 }} />
                        <div style={{ position: 'absolute', top: 0, left: 0, height: 8, width: `${scorePct}%`, background: scoreColor, borderRadius: 9999 }} />
                      </div>
                      <span style={{ fontSize: 12, color: '#6d7175', fontWeight: 500 }}>{scoreValue}/10</span>
                    </div>
                  </div>
                  
                  {/* Description below - full width */}
                  {desc && (
                    <div style={{
                      padding: '0 12px 12px 12px',
                      borderTop: '1px solid #f1f3f4',
                      marginTop: '8px',
                      paddingTop: '8px'
                    }}>
                      <span style={{
                        display: 'block',
                        color: '#6d7175',
                        fontSize: '14px',
                        lineHeight: '1.4'
                      }}>
                        {desc}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </BlockStack>
        </BlockStack>
      </Card>
    );
  };

  // Map AI-returned widget values to supported frontend widget types
  // Call 168 (Customer Feedback) uses canonical types: object_list, simple_score_cards, tag_cloud_table
  // Call 169 (Pre-sales) uses human-readable names - map them to canonical types
  const normalizeWidgetType = (raw: string): string => {
    if (!raw || typeof raw !== 'string') return raw || '';
    const lower = raw.toLowerCase().trim();
    const map: Record<string, string> = {
      call_analysis: 'tag_cloud_table',
      'call analysis': 'tag_cloud_table',
      summary: 'summary_card',
      'call summary': 'summary_card',
      activities: 'object_list',
      'actionable items': 'object_list',
      'action items': 'object_list',
      actionable_items: 'object_list',
      insights: 'simple_score_cards',
      'call insights': 'simple_score_cards',
      sentiment: 'tag_cloud_table',
      tagging: 'tag_cloud_table',
    };
    return map[lower] || raw;
  };

  const resolvedWidgetType = normalizeWidgetType(widgetType);

  // Infer widget type from items structure when type is still unknown
  const inferWidgetType = (): string => {
    if (!items || items.length === 0) return resolvedWidgetType;
    const first = items[0];
    if (typeof first === 'object') {
      const keys = Object.keys(first).map(k => k.toLowerCase());
      if (keys.some(k => ['category', 'score', 'sentiment', 'description'].includes(k))) return 'tag_cloud_table';
      if (keys.some(k => ['actionable', 'action', 'due_date', 'duedate', 'due date', 'priority', 'description'].includes(k))) return 'object_list';
      if (keys.some(k => ['overall_score', 'scores'].includes(k))) return 'detailed_score_cards';
      if (keys.some(k => ['summary', 'highlight'].includes(k))) return 'summary_card';
    }
    return resolvedWidgetType;
  };

  const finalWidgetType = ['simple_score_cards', 'summary_card', 'detailed_score_cards', 'object_list', 'tag_cloud_table'].includes(resolvedWidgetType)
    ? resolvedWidgetType
    : inferWidgetType();

  // Main render function
  const renderWidget = () => {
    switch (finalWidgetType) {
      case 'simple_score_cards':
        return renderSimpleScoreCards();
      case 'summary_card':
        return renderSummaryCard();
      case 'detailed_score_cards':
        return renderDetailedScoreCards();
      case 'object_list':
        return renderObjectList();
      case 'tag_cloud_table':
        return renderTagCloudTable();
      default:
        // Fallback: render as key-value list when we have items
        if (items && items.length > 0) {
          const first = items[0];
          const isObjectArray = typeof first === 'object' && first !== null && !Array.isArray(first);
          if (isObjectArray) {
            return (
              <Card padding="500">
                <BlockStack gap="400">
                  <Text variant="headingLg" as="h3">{title}</Text>
                  <BlockStack gap="200">
                    {items.map((item: any, idx: number) => {
                      if (typeof item !== 'object' || item === null) return null;
                      // Key/value format: {key, value} or {tag, value}
                      const k = item.key ?? item.tag ?? item.category;
                      const v = item.value ?? item.default ?? item.text;
                      if (k !== undefined) {
                        return (
                          <InlineStack key={idx} gap="200" align="start">
                            <Text variant="bodyMd" as="span" fontWeight="semibold">{formatKey(String(k))}:</Text>
                            <Text variant="bodyMd" as="span">{normalizeValue(v)}</Text>
                          </InlineStack>
                        );
                      }
                      // Generic object: show each key-value
                      return (
                        <div key={idx}>
                          {Object.entries(item)
                            .filter(([key]) => !['__ui', '__ui_metadata'].includes(key))
                            .map(([key, val]) => (
                              <InlineStack key={key} gap="200" align="start">
                                <Text variant="bodyMd" as="span" fontWeight="semibold">{formatKey(key)}:</Text>
                                <Text variant="bodyMd" as="span">
                                  {typeof val === 'object' && val !== null ? JSON.stringify(val) : normalizeValue(val)}
                                </Text>
                              </InlineStack>
                            ))}
                        </div>
                      );
                    })}
                  </BlockStack>
                </BlockStack>
              </Card>
            );
          }
        }
        return (
          <Card padding="500">
            <BlockStack gap="400">
              <Text variant="headingMd" as="h4">Unknown Widget Type: {widgetType}</Text>
              <Text variant="bodyMd" as="p">
                This widget type is not supported yet.
              </Text>
            </BlockStack>
          </Card>
        );
    }
  };

  // Remove individual tabs since we now have top-level tabs in the main page
  return renderWidget();
};

export default WidgetRenderer;
